import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useAuth } from "@/contexts/auth-context";
import { toast } from "@/components/ui/use-toast";
import { LoadingScreen } from "@/components/LoadingScreen";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsAuthenticating(true);

    try {
      await resetPassword(email);
      // Navigation is handled by the useEffect above

      toast({
        title: "Password reset link sent",
        description:
          "Please click on link in your email to reset the password!",
        variant: "default",
      });
    } catch (error) {
      console.error("Reset password error:", error);
      setIsAuthenticating(false);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to reset password!",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sidebar">
      {isAuthenticating && <LoadingScreen />}
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Creator Logo"
            className="h-12 w-auto mx-auto mb-4"
          />
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>Reset password</CardTitle>
            <CardDescription>
              Enter your email to receive a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  autoFocus
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Sending..." : "Send reset link"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                We'll send you a link to reset your password
              </p>
            </form>
          </CardContent>
          <div className="p-3 bg-zinc-100">
            <p className="text-muted-foreground text-center text-sm">
              Remembered your password?
              <Button
                asChild
                variant="link"
                className="px-2 text-muted-foreground"
              >
                <Link to="/signin" tabIndex={-1}>
                  Sign in
                </Link>
              </Button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
